import { useState, useEffect, useRef } from 'react'
import { Search, X } from 'lucide-react'
import { Input } from 'components/ui/input'
import { cn } from 'lib/utils'

export type SearchInputProps = {
	value: string
	onChange: (value: string) => void
	placeholder?: string
	/** Debounce delay in ms before onChange fires. Default 250. Pass 0 to disable. */
	debounceMs?: number
	/** Applied to the outer relative wrapper. */
	className?: string
	/** Applied to the inner Input element. */
	inputClassName?: string
}

/**
 * Search input with leading magnifier icon, trailing clear (X) button, and
 * optional debounce. Controlled: parent owns the debounced value via
 * `value` + `onChange`. Pass `debounceMs={0}` for immediate updates.
 */
export function SearchInput({
	value,
	onChange,
	placeholder = 'Search...',
	debounceMs = 250,
	className,
	inputClassName,
}: SearchInputProps) {
	const [internal, setInternal] = useState(value)
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	// Sync display when parent resets `value` externally (e.g. programmatic clear).
	useEffect(() => {
		setInternal(value)
	}, [value])

	// Cancel any pending debounce on unmount.
	useEffect(() => {
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current)
		}
	}, [])

	const emit = (next: string) => {
		setInternal(next)
		if (timerRef.current) clearTimeout(timerRef.current)
		if (debounceMs > 0) {
			timerRef.current = setTimeout(() => onChange(next), debounceMs)
		} else {
			onChange(next)
		}
	}

	const clear = () => {
		if (timerRef.current) clearTimeout(timerRef.current)
		setInternal('')
		onChange('')
	}

	return (
		<div className={cn('relative', className)}>
			<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
			<Input
				type="text"
				placeholder={placeholder}
				value={internal}
				onChange={(e) => emit(e.target.value)}
				className={cn('pl-9', internal && 'pr-9', inputClassName)}
			/>
			{internal && (
				<button
					type="button"
					onClick={clear}
					aria-label="Clear search"
					className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded"
				>
					<X className="h-4 w-4" />
				</button>
			)}
		</div>
	)
}
